import { useCallback, useEffect, useRef } from 'react';
import { getAsyncJob } from 'src/api/quests';

const DEFAULT_INTERVAL_MS = 5000;

type AsyncJob = {
    ready?: boolean;
    status?: string;
    result?: { status?: string };
};

type UseAsyncJobPollingOptions = {
    enabled?: boolean;
    intervalMs?: number;
    /** When omitted, ``onRefresh`` runs on each tick instead. */
    getJobId?: () => string | null;
    onJobReady: (job: AsyncJob) => void;
    onRefresh?: () => void;
    onError?: (error: unknown) => void;
};

/** Poll ``GET /job/:id`` until ``ready``, then invoke ``onJobReady``. */
export function useAsyncJobPolling({
    enabled = true,
    intervalMs = DEFAULT_INTERVAL_MS,
    getJobId,
    onJobReady,
    onRefresh,
    onError
}: UseAsyncJobPollingOptions) {
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const onJobReadyRef = useRef(onJobReady);
    const onRefreshRef = useRef(onRefresh);
    const onErrorRef = useRef(onError);

    onJobReadyRef.current = onJobReady;
    onRefreshRef.current = onRefresh;
    onErrorRef.current = onError;

    const stopPolling = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            stopPolling();
            return stopPolling;
        }

        const poll = async () => {
            const jobId = getJobId?.() ?? null;
            if (jobId) {
                try {
                    const response = await getAsyncJob(jobId);
                    const job = response?.data as AsyncJob | undefined;
                    if (job?.ready) {
                        stopPolling();
                        onJobReadyRef.current(job);
                    }
                } catch (error) {
                    stopPolling();
                    onErrorRef.current?.(error);
                }
                return;
            }

            onRefreshRef.current?.();
        };

        timerRef.current = setInterval(poll, intervalMs);
        return stopPolling;
    }, [enabled, getJobId, intervalMs, stopPolling]);

    const startPolling = useCallback(
        (jobId: string) => {
            stopPolling();
            timerRef.current = setInterval(async () => {
                try {
                    const response = await getAsyncJob(jobId);
                    const job = response?.data as AsyncJob | undefined;
                    if (job?.ready) {
                        stopPolling();
                        onJobReadyRef.current(job);
                    }
                } catch (error) {
                    stopPolling();
                    onErrorRef.current?.(error);
                }
            }, intervalMs);
        },
        [intervalMs, stopPolling]
    );

    return { startPolling, stopPolling };
}
